import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchProduct } from "../lib/api";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Sparkles, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatUAH } from "../lib/format";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();

  useEffect(() => {
    fetchProduct(slug).then((p) => {
      setProduct(p);
      setSize(p.sizes[0]);
      setColor(p.colors[0]);
      // Preload all color images so switching is instant (no network delay)
      const urls = p.color_images ? Object.values(p.color_images).flat() : p.images;
      urls.forEach((src) => { const im = new Image(); im.src = src; });
    }).catch(console.error);
  }, [slug]);

  if (!product) return <div className="py-32 text-center text-silea-navy/60">Завантаження...</div>;

  if (product.coming_soon) {
    return (
      <div data-testid="product-coming-soon" className="bg-silea-cream">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-32 text-center">
          <p className="eyebrow text-silea-navy/50">{product.name_short}</p>
          <h1 className="font-serif-display text-5xl sm:text-7xl mt-4 text-silea-navy leading-[1.05]">
            Coming <span className="italic-sing text-silea-yellow">soon</span>
          </h1>
          <p className="mt-6 max-w-md mx-auto text-silea-navy/70">
            Ця модель скоро з'явиться в колекції. Слідкуйте за оновленнями.
          </p>
          <Link
            to="/products"
            data-testid="back-to-collection"
            className="inline-flex items-center gap-3 mt-10 bg-silea-navy text-white px-8 py-4 eyebrow hover:bg-silea-yellow hover:text-silea-navy transition-colors duration-500"
          >
            Повернутись до колекції
          </Link>
        </div>
      </div>
    );
  }

  const unitPrice = product.price;
  const total = unitPrice * qty;

  const galleryImages = (product.color_images && product.color_images[color]) || product.images;

  const onSelectColor = (c) => {
    setColor(c);
    setActiveImage(0);
  };

  const onAdd = () => {
    if (!size || !color) {
      toast.error("Оберіть розмір та колір");
      return;
    }

    addItem({
      product_id: product.id,
      product_name: product.name_short,
      base_price: product.price,
      size, color, quantity: qty,
      customizations: [],
      image: galleryImages[0],
    });
    toast.success("Додано до кошика", { description: `${product.name_short} · ${color} · ${size}` });
  };

  return (
    <div data-testid="product-detail-page" className="bg-silea-cream">
      {/* Breadcrumb */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-8 pb-4 eyebrow text-silea-navy/50">
        <Link to="/" className="hover:text-silea-navy">Головна</Link>
        <span className="mx-2">/</span>
        <Link to="/products" className="hover:text-silea-navy">Колекція</Link>
        <span className="mx-2">/</span>
        <span className="text-silea-navy">{product.name_short}</span>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 pb-24">
        {/* Gallery */}
        <div className="lg:col-span-7">
          <div className="aspect-[4/5] overflow-hidden bg-stone-100">
            <img src={galleryImages[activeImage]} alt={product.name} fetchpriority="high" className="w-full h-full object-cover" />
          </div>
          {galleryImages.length > 1 && (
            <div className="grid grid-cols-4 gap-3 mt-3">
              {galleryImages.map((src, i) => (
                <button
                  key={src}
                  onClick={() => setActiveImage(i)}
                  data-testid={`thumb-${i}`}
                  className={`aspect-square overflow-hidden border ${activeImage === i ? "border-silea-navy" : "border-transparent"}`}
                >
                  <img src={src} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
          <p className="eyebrow text-silea-navy/60">{product.category === "bikini" ? "Бікіні" : "Суцільний"}</p>
          <h1 className="font-serif-display text-5xl sm:text-6xl mt-3 text-silea-navy leading-[1.05]">
            {product.name_short}
          </h1>
          <p className="mt-3 text-silea-navy/70" data-testid="product-color-desc">
            {(product.color_descriptions && product.color_descriptions[color]) || product.short_description}
          </p>

          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-serif-display text-3xl text-silea-navy" data-testid="product-price">
              {formatUAH(unitPrice)}
            </span>
          </div>

          {/* Color */}
          <div className="mt-8">
            <p className="eyebrow text-silea-navy/60 mb-3">Колір — <span className="text-silea-navy">{color}</span></p>
            <div className="flex gap-2">
              {product.colors.map((c) => (
                <button
                  key={c}
                  onClick={() => onSelectColor(c)}
                  data-testid={`color-${c}`}
                  className={`px-4 py-2 text-xs border ${color === c ? "border-silea-navy bg-silea-navy text-white" : "border-stone-300 text-silea-navy hover:border-silea-navy"}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Size */}
          <div className="mt-6">
            <p className="eyebrow text-silea-navy/60 mb-3">Розмір</p>
            <div className="flex gap-2">
              {product.sizes.map((s) => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  data-testid={`size-${s}`}
                  className={`w-12 h-12 border text-sm ${size === s ? "border-silea-navy bg-silea-navy text-white" : "border-stone-300 text-silea-navy hover:border-silea-navy"}`}
                >
                  {s}
                </button>
              ))}
            </div>
            {size === "L" && (
              <p className="mt-3 text-xs text-silea-navy/70" data-testid="size-l-note">
                Розмір L виготовляємо під замовлення — 7–10 робочих днів.
              </p>
            )}
          </div>

          {/* Customization CTA */}
          <Link
            to="/customizer"
            data-testid="open-customizer-cta"
            className="mt-10 group flex items-center justify-between gap-4 border border-silea-navy bg-white hover:bg-silea-navy hover:text-white transition-colors duration-500 px-5 py-4"
          >
            <div className="flex items-center gap-3">
              <Sparkles size={18} className="text-silea-yellow" strokeWidth={1.6} />
              <div className="text-left">
                <p className="eyebrow text-silea-navy group-hover:text-white">Створіть власний дизайн</p>
                <p className="text-xs text-silea-navy/60 group-hover:text-white/70 mt-1">
                  Перейти до інтерактивного кастомайзера
                </p>
              </div>
            </div>
            <ArrowRight size={18} strokeWidth={1.6} className="shrink-0" />
          </Link>

          {/* Quantity + Add to cart */}
          <div className="mt-8 flex items-stretch gap-3">
            <div className="inline-flex items-center border border-stone-300">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4 text-silea-navy" data-testid="qty-decrease">−</button>
              <span className="px-4 text-sm">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="px-4 text-silea-navy" data-testid="qty-increase">+</button>
            </div>
            <Button
              onClick={onAdd}
              data-testid="add-to-cart-btn"
              className="flex-1 bg-silea-navy hover:bg-silea-navy/90 text-white rounded-none h-12 eyebrow"
            >
              Додати — {formatUAH(total)}
            </Button>
          </div>

          {/* Accordion */}
          <Accordion type="single" collapsible className="mt-10">
            <AccordionItem value="desc" className="border-stone-200">
              <AccordionTrigger className="eyebrow text-silea-navy">Опис</AccordionTrigger>
              <AccordionContent className="text-silea-navy/70 leading-relaxed">
                {product.description}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="fabric" className="border-stone-200">
              <AccordionTrigger className="eyebrow text-silea-navy">Тканина</AccordionTrigger>
              <AccordionContent className="text-silea-navy/70">{product.fabric}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="care" className="border-stone-200">
              <AccordionTrigger className="eyebrow text-silea-navy">Догляд</AccordionTrigger>
              <AccordionContent className="text-silea-navy/70">{product.care}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="ship" className="border-stone-200">
              <AccordionTrigger className="eyebrow text-silea-navy">Доставка</AccordionTrigger>
              <AccordionContent className="text-silea-navy/70">
                Доставка Новою Поштою по Україні. Кастомні замовлення виготовляються 5–10 робочих днів.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </div>
  );
}
